import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cacheGet, cacheSet, isNetworkError } from "@/lib/offline";
import { useLang } from "@/lib/i18n";
import { EmptyRow, Money, PageHeader, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function Invoices() {
  const { t } = useLang();
  const nav = useNavigate();
  const [rows, setRows] = useState(() => cacheGet("invoices") || []);
  const [cached, setCached] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/invoices").then((r) => { setRows(r.data); cacheSet("invoices", r.data); setCached(false); })
    .catch((e) => { if (isNetworkError(e) && cacheGet("invoices")) setCached(true); });
  useEffect(() => { load(); }, []);

  const toggle = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.id}/status`, { status: inv.status === "paid" ? "unpaid" : "paid" });
      toast.success(t("saved"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (inv) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/invoices/${inv.id}`);
    toast.success(t("deleted"));
    load();
  };
  const shown = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="invoices-page">
      <PageHeader title={t("invoices")} subtitle={t("invoicesSubtitle")}
        actions={<Button asChild data-testid="new-invoice-btn" className="bg-[#007A3D] hover:bg-[#005E2E]"><Link to="/invoices/new"><Plus className="me-1 h-4 w-4" />{t("newInvoice")}</Link></Button>} />
      <div className="mb-4 flex gap-1">
        {cached && <span data-testid="invoices-cached-badge" className="me-2 self-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">{t("cachedData")}</span>}
        {["all", "draft", "unpaid", "paid"].map((f) => (
          <button key={f} data-testid={`invoice-filter-${f}`} onClick={() => setFilter(f)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === f ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"}`}>
            {t(f)}
          </button>
        ))}
      </div>
      <Panel className="overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>{t("invoiceNumber")}</th><th>{t("client")}</th><th>{t("issueDate")}</th><th>{t("dueDate")}</th><th>{t("status")}</th><th className="text-end">{t("subtotal")}</th><th className="text-end">{t("vat")}</th><th className="text-end">{t("total")}</th><th className="text-end">{t("actions")}</th></tr></thead>
          <tbody>
            {shown.length === 0 && <EmptyRow cols={9} />}
            {shown.map((i) => (
              <tr key={i.id} data-testid={`invoice-row-${i.number || `draft-${i.id}`}`} className="cursor-pointer" onClick={() => nav(`/invoices/${i.id}`)}>
                <td className="font-mono font-semibold text-slate-900">{i.number || <span className="text-xs uppercase tracking-wider text-slate-400">{t("draft")}</span>}{i.currency && i.currency !== "AED" && <span className="ms-2 rounded bg-slate-100 px-1.5 py-0.5 font-sans text-[10px] font-bold text-slate-600">{i.currency}</span>}</td>
                <td>{i.client_name}</td>
                <td className="font-mono text-slate-500">{i.issue_date}</td>
                <td className="font-mono text-slate-500">{i.due_date}</td>
                <td><StatusBadge status={i.status} /></td>
                <td className="text-end"><Money value={i.subtotal} cur={false} /></td>
                <td className="text-end"><Money value={i.vat_total} cur={false} /></td>
                <td className="text-end font-semibold"><Money value={i.total} /></td>
                <td className="whitespace-nowrap text-end" onClick={(e) => e.stopPropagation()}>
                  {i.status !== "draft" && <button data-testid={`toggle-paid-${i.number}`} title={t(i.status === "paid" ? "markUnpaid" : "markPaid")} onClick={() => toggle(i)} className="rounded p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-[#007A3D]">
                    {i.status === "paid" ? <RotateCcw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                  </button>}
                  <button data-testid={`delete-invoice-${i.number || `draft-${i.id}`}`} onClick={() => remove(i)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
